// @ts-nocheck
/**
 * ツインキーシステム Context
 * グローバル状態管理
 * @module twinKey/context/TwinKeyContext
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';

// ============================================================
// 画面定数
// ============================================================

export const SCREENS = {
  HUB: 'hub',
  KEY_SELECTION: 'keySelection',
  PARTY: 'party',
  DUNGEON: 'dungeon',
  BATTLE: 'battle',
  RESULT: 'result',
  ALCHEMY: 'alchemy',
};

// ============================================================
// 初期状態
// ============================================================

const initialState = {
  // 画面状態
  screen: SCREENS.HUB,

  // 鍵選択
  key1: null,
  key2: null,

  // パーティ
  activeParty: null,

  // ダンジョン
  currentDungeon: null,
  dungeonPreview: null,

  // バトル
  currentBattle: null,
  battleResult: null,

  // 探索結果
  expeditionResult: null,

  // フロアイベント結果（罠、宝箱、イベント、休息など）
  floorEventResult: null,

  // ローディング
  isLoading: false,
  error: null,
};

// ============================================================
// アクションタイプ
// ============================================================

export const ACTION_TYPES = {
  // 画面遷移
  SET_SCREEN: 'SET_SCREEN',

  // 鍵選択
  SET_KEY1: 'SET_KEY1',
  SET_KEY2: 'SET_KEY2',
  CLEAR_KEYS: 'CLEAR_KEYS',
  SWAP_KEYS: 'SWAP_KEYS',

  // パーティ
  SET_ACTIVE_PARTY: 'SET_ACTIVE_PARTY',

  // ダンジョン
  SET_DUNGEON_PREVIEW: 'SET_DUNGEON_PREVIEW',
  START_DUNGEON: 'START_DUNGEON',
  UPDATE_DUNGEON: 'UPDATE_DUNGEON',
  END_DUNGEON: 'END_DUNGEON',

  // バトル
  START_BATTLE: 'START_BATTLE',
  UPDATE_BATTLE: 'UPDATE_BATTLE',
  END_BATTLE: 'END_BATTLE',

  // 探索結果
  SET_EXPEDITION_RESULT: 'SET_EXPEDITION_RESULT',
  CLEAR_EXPEDITION_RESULT: 'CLEAR_EXPEDITION_RESULT',

  // フロアイベント結果
  SET_FLOOR_EVENT_RESULT: 'SET_FLOOR_EVENT_RESULT',
  CLEAR_FLOOR_EVENT_RESULT: 'CLEAR_FLOOR_EVENT_RESULT',

  // ローディング
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',

  // リセット
  RESET: 'RESET',
};

// ============================================================
// Reducer
// ============================================================

function twinKeyReducer(state, action) {
  switch (action.type) {
    // 画面遷移
    case ACTION_TYPES.SET_SCREEN:
      return { ...state, screen: action.payload, error: null };

    // 鍵選択
    case ACTION_TYPES.SET_KEY1:
      return { ...state, key1: action.payload };

    case ACTION_TYPES.SET_KEY2:
      return { ...state, key2: action.payload };

    case ACTION_TYPES.CLEAR_KEYS:
      return { ...state, key1: null, key2: null, dungeonPreview: null };

    case ACTION_TYPES.SWAP_KEYS:
      return { ...state, key1: state.key2, key2: state.key1 };

    // パーティ
    case ACTION_TYPES.SET_ACTIVE_PARTY:
      return { ...state, activeParty: action.payload };

    // ダンジョン
    case ACTION_TYPES.SET_DUNGEON_PREVIEW:
      return { ...state, dungeonPreview: action.payload };

    case ACTION_TYPES.START_DUNGEON:
      return {
        ...state,
        currentDungeon: action.payload,
        screen: 'dungeon',
      };

    case ACTION_TYPES.UPDATE_DUNGEON:
      return { ...state, currentDungeon: action.payload };

    case ACTION_TYPES.END_DUNGEON:
      return {
        ...state,
        expeditionResult: action.payload,
        currentDungeon: null,
        screen: 'result',
      };

    // バトル
    case ACTION_TYPES.START_BATTLE:
      return {
        ...state,
        currentBattle: action.payload,
        battleResult: null,
        screen: 'battle',
      };

    case ACTION_TYPES.UPDATE_BATTLE:
      return { ...state, currentBattle: action.payload };

    case ACTION_TYPES.END_BATTLE:
      return {
        ...state,
        battleResult: action.payload,
        currentBattle: null,
      };

    // 探索結果
    case ACTION_TYPES.SET_EXPEDITION_RESULT:
      return { ...state, expeditionResult: action.payload };

    case ACTION_TYPES.CLEAR_EXPEDITION_RESULT:
      return { ...state, expeditionResult: null };

    // フロアイベント結果
    case ACTION_TYPES.SET_FLOOR_EVENT_RESULT:
      return { ...state, floorEventResult: action.payload };

    case ACTION_TYPES.CLEAR_FLOOR_EVENT_RESULT:
      return { ...state, floorEventResult: null };

    // ローディング
    case ACTION_TYPES.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case ACTION_TYPES.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };

    case ACTION_TYPES.CLEAR_ERROR:
      return { ...state, error: null };

    // リセット
    case ACTION_TYPES.RESET:
      return { ...initialState };

    default:
      return state;
  }
}

// ============================================================
// Context
// ============================================================

const TwinKeyContext = createContext(null);

// ============================================================
// Provider
// ============================================================

/**
 * TwinKeyProvider
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {Object} props.playerData - プレイヤーデータ
 * @param {Function} props.updatePlayerData - プレイヤーデータ更新関数
 * @param {Array} props.allCards - 全カードデータ
 * @param {Function} props.onBack - 戻るボタンのコールバック
 * @param {Function} props.onDungeonComplete - ダンジョン完了時のコールバック（日付進行処理）
 */
export function TwinKeyProvider({
  children,
  playerData,
  updatePlayerData,
  allCards,
  onBack,
  onDungeonComplete,
}) {
  const [state, dispatch] = useReducer(twinKeyReducer, initialState);

  // ========================================
  // Android 戻るボタン対応（History API）
  // ========================================

  // TwinKey内の画面遷移の親子関係
  const TWINKEY_SCREEN_HIERARCHY = {
    'hub': null,           // ハブ画面（戻るとメインメニューへ）
    'keySelection': 'hub', // 鍵選択 → ハブ
    'party': 'hub',        // パーティ → ハブ
    'alchemy': 'hub',      // 錬金 → ハブ
    'dungeon': null,       // ダンジョン中は戻れない
    'battle': null,        // バトル中は戻れない
    'result': 'hub',       // 結果 → ハブ
  };

  // popstateからの遷移かどうかを追跡（二重履歴追加防止）
  const isPopstateTransitionRef = useRef(false);
  const prevScreenRef = useRef(state.screen);

  // 画面変更時に自動でhistory追加
  useEffect(() => {
    // popstateからの遷移の場合は履歴を追加しない
    if (isPopstateTransitionRef.current) {
      isPopstateTransitionRef.current = false;
      prevScreenRef.current = state.screen;
      return;
    }

    // 画面が変わった場合にのみ履歴を追加
    if (prevScreenRef.current !== state.screen) {
      window.history.pushState(
        { screen: 'twinKey', twinKeyScreen: state.screen, timestamp: Date.now() },
        '',
        ''
      );
      prevScreenRef.current = state.screen;
    }
  }, [state.screen]);

  // popstateイベントハンドラ（戻るボタン押下時）
  useEffect(() => {
    const handlePopState = (event) => {
      // TwinKey内の画面状態を確認
      const historyState = event.state;

      // TwinKey以外の画面への遷移の場合は無視（親のハンドラに任せる）
      if (!historyState?.twinKeyScreen && historyState?.screen !== 'twinKey') {
        return;
      }

      const currentScreen = state.screen;
      const parentScreen = TWINKEY_SCREEN_HIERARCHY[currentScreen];

      // 戻れない画面（dungeon, battle）の場合
      if (parentScreen === null && currentScreen !== 'hub') {
        // 履歴を元に戻す（戻るボタンを無効化）
        window.history.pushState(
          { screen: 'twinKey', twinKeyScreen: currentScreen, timestamp: Date.now() },
          '',
          ''
        );
        return;
      }

      // ハブ画面で戻るボタンが押された場合
      if (currentScreen === 'hub') {
        // メインメニューに戻る
        if (onBack) {
          onBack();
        }
        return;
      }

      // 親画面に戻る
      if (parentScreen) {
        isPopstateTransitionRef.current = true;
        dispatch({ type: ACTION_TYPES.SET_SCREEN, payload: parentScreen });
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [state.screen, onBack]);

  // アクション作成ヘルパー
  const actions = {
    // 画面遷移
    setScreen: useCallback((screen) => {
      dispatch({ type: ACTION_TYPES.SET_SCREEN, payload: screen });
    }, []),

    goToHub: useCallback(() => {
      dispatch({ type: ACTION_TYPES.SET_SCREEN, payload: 'hub' });
    }, []),

    // 鍵選択
    setKey1: useCallback((key) => {
      dispatch({ type: ACTION_TYPES.SET_KEY1, payload: key });
    }, []),

    setKey2: useCallback((key) => {
      dispatch({ type: ACTION_TYPES.SET_KEY2, payload: key });
    }, []),

    clearKeys: useCallback(() => {
      dispatch({ type: ACTION_TYPES.CLEAR_KEYS });
    }, []),

    swapKeys: useCallback(() => {
      dispatch({ type: ACTION_TYPES.SWAP_KEYS });
    }, []),

    // パーティ
    setActiveParty: useCallback((party) => {
      dispatch({ type: ACTION_TYPES.SET_ACTIVE_PARTY, payload: party });
    }, []),

    // ダンジョン
    setDungeonPreview: useCallback((preview) => {
      dispatch({ type: ACTION_TYPES.SET_DUNGEON_PREVIEW, payload: preview });
    }, []),

    startDungeon: useCallback((dungeon) => {
      dispatch({ type: ACTION_TYPES.START_DUNGEON, payload: dungeon });
    }, []),

    updateDungeon: useCallback((dungeon) => {
      dispatch({ type: ACTION_TYPES.UPDATE_DUNGEON, payload: dungeon });
    }, []),

    endDungeon: useCallback((result) => {
      dispatch({ type: ACTION_TYPES.END_DUNGEON, payload: result });
    }, []),

    // バトル
    startBattle: useCallback((battleState) => {
      dispatch({ type: ACTION_TYPES.START_BATTLE, payload: battleState });
    }, []),

    updateBattle: useCallback((battleState) => {
      dispatch({ type: ACTION_TYPES.UPDATE_BATTLE, payload: battleState });
    }, []),

    endBattle: useCallback((result) => {
      dispatch({ type: ACTION_TYPES.END_BATTLE, payload: result });
    }, []),

    // フロアイベント結果
    setFloorEventResult: useCallback((result) => {
      dispatch({ type: ACTION_TYPES.SET_FLOOR_EVENT_RESULT, payload: result });
    }, []),

    clearFloorEventResult: useCallback(() => {
      dispatch({ type: ACTION_TYPES.CLEAR_FLOOR_EVENT_RESULT });
    }, []),

    // ローディング
    setLoading: useCallback((isLoading) => {
      dispatch({ type: ACTION_TYPES.SET_LOADING, payload: isLoading });
    }, []),

    setError: useCallback((error) => {
      dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error });
    }, []),

    clearError: useCallback(() => {
      dispatch({ type: ACTION_TYPES.CLEAR_ERROR });
    }, []),

    // リセット
    reset: useCallback(() => {
      dispatch({ type: ACTION_TYPES.RESET });
    }, []),
  };

  const value = {
    // 状態
    state,
    dispatch,

    // アクション
    actions,

    // 外部データ
    playerData,
    updatePlayerData,
    allCards,
    onBack,
    onDungeonComplete,

    // ヘルパー
    getCardById: useCallback((cardId) => {
      return allCards?.find(c => c.id === cardId);
    }, [allCards]),

    getCollectionCard: useCallback((uniqueId) => {
      return playerData?.collection?.find(c => c.uniqueId === uniqueId);
    }, [playerData]),
  };

  return (
    <TwinKeyContext.Provider value={value}>
      {children}
    </TwinKeyContext.Provider>
  );
}

// ============================================================
// Hook
// ============================================================

/**
 * TwinKey Context を使用するフック
 * @returns {Object} Context値
 */
export function useTwinKeyContext() {
  const context = useContext(TwinKeyContext);
  if (!context) {
    throw new Error('useTwinKeyContext must be used within TwinKeyProvider');
  }
  return context;
}

export default TwinKeyContext;
